import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { selectOrganizations, selectCurrentOrgId } from "../../../store/models/Organization";
import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader";
import PageTitleBar from "../../../components/PageTitleBar";
import { Button } from "../../../components/Button";
import User, {
  getUserFullName,
  selectCurrentUser,
  selectCurrentUserId,
  selectCurrentUserRole
} from "../../../store/models/User";
import { EditIcon, CloseIcon, DeleteIcon } from "../../../components/Svg";
import { ModalTypes, UserRoleIds, Urls } from "../../../constants";
import UIState from "../../../store/UIState";
import Project, { selectAllProjects, selectCurrentProjectId } from "../../../store/models/Project";
import _ from "lodash";
import cx from "classnames";
import { UserAvatar } from "../../../components/UserAvatar";
import { pushTo } from "../../../store/history";
import { toast } from "react-toastify";

const selector = createSelector(
  selectCurrentUserId,
  selectCurrentUser,
  selectCurrentOrgId,
  selectCurrentProjectId,
  (state) => selectAllProjects(state),
  (state) => selectOrganizations(state),
  (currentUserId, currentUser, currentOrgId, currentProjectId, projects, orgs) => {
    return {
      currentUserId,
      currentUser,
      currentOrgId,
      currentProjectId,
      projects: _.values(projects),
      orgs
    }
  }
)

export const ProfilePage = () => {

  const dispatch = useDispatch();
  const { currentUserId, currentUser, currentOrgId, currentProjectId, orgs, projects } = useSelector(state => selector(state));
  const role = useSelector(state => selectCurrentUserRole(state));

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const currentUserIsAdmin = role === UserRoleIds.ADMIN;

  useEffect(() => {
    fetchData()
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        dispatch(User.actions.fetchUser(currentUserId)),
        dispatch(Project.actions.fetchUserProjects(currentUserId, { expand: 'org_id' }))
      ])
    } catch (e) {
      setError(e.message)
    }
    setLoading(false);
  }

  const onEditProfile = () => {
    dispatch(UIState.actions.showModal(ModalTypes.EDIT_PROFILE))
  }


  const onEditOrg = (org) => {
    dispatch(UIState.actions.showModal(ModalTypes.EDIT_ORG, { org }))
  }

  const onEditProject = (project, e) => {
    e.stopPropagation(); // Prevent navigation when clicking the edit button
    dispatch(UIState.actions.showModal(ModalTypes.EDIT_PROJECT, { project }))
  }

  const navigateToProject = (projectId) => {
    pushTo(Urls.SETTINGS_PROJECT.replace(':projectId', projectId));
  }

  const onDeleteProject = (project, e) => {
    e.stopPropagation();
    const onConfirm = async () => {
      try {
        await dispatch(Project.actions.deleteProject(project));
        await fetchData();
        toast.success('Project deleted successfully');
      } catch (e) {
        toast.error(`Failed to delete project: ${e.message}`);
      }
    };
    dispatch(UIState.actions.showModal(ModalTypes.DELETE, { title: project.name, onConfirm }));
  };

  // Sort organizations: alphabetical, current org first, disabled orgs last
  const sortedOrgs = React.useMemo(() => {
    return _.orderBy(orgs, [
      // First sorting criteria: disabled status (false comes before true)
      org => org.disabled ? 1 : 0,
      // Second sorting criteria: current org status
      org => org.id === currentOrgId ? 0 : 1,
      // Third sorting criteria: alphabetical by name
      org => org.name.toLowerCase()
    ]);
  }, [orgs, currentOrgId]);

  if (loading)
    return <Loader />

  if (error)
    return (<div className={"ServicePage__Error"}>
      {error}
      <Button onClick={fetchData()}
        color={"green-outline"}
        title={"Retry"} />
    </div>)

  return (<div className={"ProfilePage"}>
    <PageTitleBar alignTitleLeft={true} title={"Profile"} />

    <div className={"ProfilePage__UserBlock"}>
      <UserAvatar user={currentUser} size={'l'} />
      <div className={"ProfilePage__UserBlock__Info"}>
        <div className={"ProfilePage__UserBlock__Info__name"}>{getUserFullName(currentUser)}</div>
        <div className={"ProfilePage__UserBlock__Info__email"}>{currentUser?.email}</div>
        <div className={"ProfilePage__UserBlock__Info__email"}>User ID: {currentUser?.id}</div>
      </div>
      <div className={"ProfilePage__UserBlock__Edit"} onClick={onEditProfile}>
        <EditIcon />
      </div>
    </div>

    <div className="ProfilePage__Label">Your organizations</div>

    {_.map(sortedOrgs, org => {
      // Get projects for this org
      let orgProjects = _.filter(projects, project => project.org_id === org.id);

      // Sort projects alphabetically, with current project at the top
      orgProjects = _.orderBy(orgProjects, [
        project => project.id === currentProjectId ? 0 : 1,
        project => project.name.toLowerCase()
      ]);

      return (<div className={"ProfilePage__Block"} key={org.id}>
        <div className={'ProfilePage__Block__Items'}>
          <div key={org.id}
            className={cx("ProfilePage__Block__Item ProfilePage__Block__Item--Header")}>
            <div className={"ProfilePage__Block__Item__Name"}>
              {org.name}
              {org.id === currentOrgId && <span className="ProfilePage__Block__Item__Name__Current">Current</span>}
              {org.disabled && <span className="ProfilePage__Block__Item__Name__Disabled">Disabled</span>}
            </div>
            <div className={"ProfilePage__Block__Item__Id"}>Organization ID: {org.id}</div>
            <div className="ProfilePage__Block__Item__Actions">
              {currentUserIsAdmin && <div className={"ProfilePage__Block__Item__Edit"} onClick={() => onEditOrg(org)}>
                <EditIcon />
              </div>}
            </div>
          </div>

          {!org.disabled && (
            <>
              <div className={"ProfilePage__Block__Item ProfilePage__Block__Item--Project ProfilePage__Block__Item--Label"}>Projects</div>

              {_.map(orgProjects, project => {
                const isLast = _.last(orgProjects) === project
                return (<div key={project.id}
                  className={cx("ProfilePage__Block__Item ProfilePage__Block__Item--Project", `ProfilePage__Block__Item--${isLast ? 'last' : ''}`, "ProfilePage__Block__Item--Clickable")}
                  onClick={() => navigateToProject(project.id)}>
                  <div className={"ProfilePage__Block__Item__Name"}>
                    {project.name} {project.id === currentProjectId && <span className="ProfilePage__Block__Item__Name__Current">Current</span>}
                  </div>
                  <div className={"ProfilePage__Block__Item__Id"}>Project ID: {project.id}</div>

                  {currentUserIsAdmin && (
                    <div className={"ProfilePage__Block__Item__Actions"}>
                      <div className={"ProfilePage__Block__Item__Delete"} onClick={(e) => onDeleteProject(project, e)}>
                        <DeleteIcon />
                      </div>
                      <div className={"ProfilePage__Block__Item__Edit"} onClick={(e) => onEditProject(project, e)}>
                        <EditIcon />
                      </div>
                    </div>
                  )}
                </div>)
              })}
            </>
          )}
        </div>
      </div>)
    })}
  </div>)
}
